import { ArticleCreation, jArticleLittle } from '../../types/article';
import { jUserAutocomplete } from '../../types/autocomplete';
import { jBusinessContactList, jBusinessContactLittle } from '../../types/businessContact';
import { FormatExtension } from '../../types/extensions';
import { jGroupLittle } from '../../types/group';
import { jObjectLittle } from '../../types/objectBase';
import {
    BusinessContact,
    jUserLittle,
    jUserValues,
    jUserView,
    UserDirectoryConfiguration,
    UserField,
    UserFieldsConfigurationType,
    UserFieldsType,
    UserMapModel,
    UserNotificationsConfiguration,
} from '../../types/user';
import {
    ApiDefaultWrapper,
    ApiPagingResults,
    ApiWrapper,
    FormatList,
    ObjectListProps,
    Readable,
} from '../../types/utils';
import { OF } from '../../util/network';
import { combineFormat } from '../../util/object';
import { JBaseApi } from '../base';

export class JUserApi extends JBaseApi {
    /**
     * autocomplete return a list users
     * @param query <string>
     */
    public autocomplete<T = jUserAutocomplete>(query: string, limit?: number, format?: FormatList) {
        return this.network
            .post<Array<T>>({
                o: 'autocomplete',
                f: 'user',
                q: query,
                idToUri: false,
                limit,
                format,
            } as OF)
            .then((res) => {
                return res.result;
            });
    }

    /**
     * Get a user
     * @param uri uri
     * @param format little by default. If another format is provided, the generic types should be defined
     */
    public get<T extends jObjectLittle = jObjectLittle>(
        uri: string,
        format = 'little',
        signal?: AbortSignal,
    ): Promise<ApiWrapper<T>> {
        return this.network.post<T>({ o: 'user', f: 'get', uri, format } as OF, signal);
    }

    /**
     * On upload file component, do to forget to set attrName to image
     * @param idFile
     */
    public updateImage(idFile: number, format = 'raw-little'): Promise<ApiWrapper<jObjectLittle>> {
        return this.network.postCSRF({
            o: 'user',
            f: 'updateImage',
            idFile,
            format,
        } as OF);
    }

    /**
     * On upload file component, do to forget to set attrName to userBanner
     * @param idFile
     */
    public updateBanner(idFile: number, format = 'raw-little'): Promise<ApiWrapper<jObjectLittle>> {
        return this.network.postCSRF({
            o: 'user',
            f: 'updateBanner',
            idFile,
            format,
        } as OF);
    }

    /**
     * Update mail
     * @param mail as Mail
     * @param password as Password
     */
    public updateMail(mail: string, password: string): Promise<ApiWrapper<{ success: boolean }>> {
        return this.network.postCSRF({
            o: 'user',
            f: 'editMail',
            mail,
            password,
        } as OF);
    }

    public checkPassword(password: string, signal?: AbortSignal): Promise<ApiWrapper<{ success: boolean }>> {
        return this.network.postCSRF(
            {
                o: 'user',
                f: 'checkPassword',
                password,
            } as OF,
            signal,
        );
    }

    public userUpdateProfile(values: jUserValues): Promise<ApiWrapper<jUserLittle>> {
        return this.network.postCSRF<jUserLittle>({
            o: 'user',
            f: 'updateProfile',
            ...values,
        } as OF);
    }

    public configuration(): Promise<ApiWrapper<UserDirectoryConfiguration>> {
        return this.network.post({ o: 'userDirectory', f: 'configuration' } as OF);
    }

    public fieldsConfiguration(): Promise<ApiWrapper<UserFieldsConfigurationType>> {
        return this.network.post({ o: 'user', f: 'fieldsConfiguration' } as OF);
    }

    public userList<T extends jObjectLittle = jUserLittle>(
        payload: ObjectListProps,
        params?: { signal?: AbortSignal; formatExtension?: FormatExtension },
    ): Promise<ApiPagingResults<T>> {
        return this.network.post(
            {
                o: 'objectList',
                f: 'JamespotUserList',
                ...payload,
                format: combineFormat(payload.format, params?.formatExtension),
            } as OF,
            params?.signal,
        );
    }

    public userQueryList<T extends jObjectLittle = jUserLittle>(
        payload: ObjectListProps,
        params?: { signal?: AbortSignal; formatExtension?: FormatExtension },
    ): Promise<ApiPagingResults<T>> {
        return this.network.post({
            o: 'objectList',
            f: 'JamespotUserQueryList',
            ...payload,
            format: combineFormat(payload.format, params?.formatExtension),
        } as OF);
    }

    public userMap(payload: ObjectListProps): Promise<ApiPagingResults<UserMapModel>> {
        return this.network.post({ o: 'objectListJamespot', f: 'userMap', ...payload } as OF);
    }

    public userBusinessContactQueryList<T extends jObjectLittle = jBusinessContactLittle>(
        query: ObjectListProps,
    ): Promise<ApiPagingResults<T>> {
        return this.network.post({ o: 'businessContact', f: 'queryList', ...query } as OF);
    }

    public userCreateBusinessContact<T extends jArticleLittle = jArticleLittle>(
        user: BusinessContact,
        options?: {
            format?: FormatList;
        },
    ): Promise<ApiWrapper<T[]>> {
        const userFormatter = {
            ...user,
            pseudo: user.title,
            firstName: user.firstname,
            lastName: user.lastname,
            champ1: user.field1,
            champ2: user.field2,
            champ3: user.field3,
            champ4: user.field4,
            champ5: user.field5,
        };
        const payload = {
            ...userFormatter,
            type: 'businessContactCard',
            publishTo: 'networkPublic/1', // TEMPORARY TILL I FIX J.networks.networkPublic.uri
        };
        return this.jApi.article.create<T>(payload as ArticleCreation, options?.format ?? 'raw-little');
    }

    public userUpdateBusinessContact<T extends jBusinessContactLittle = jBusinessContactList>(
        user: Readable<
            BusinessContact & {
                uri: string;
            }
        >,
        options?: { format?: FormatList },
    ): Promise<ApiWrapper<T>> {
        const userFormatter = {
            ...user,
            pseudo: user.title,
            firstName: user.firstname,
            lastName: user.lastname,
            champ1: user.field1,
            champ2: user.field2,
            champ3: user.field3,
            champ4: user.field4,
            champ5: user.field5,
        };
        const payload = {
            ...userFormatter,
            uri: user.uri,
            publishTo: 'networkPublic/1', // TEMPORARY TILL I FIX J.networks.networkPublic.uri
        };
        return this.jApi.article.update(payload as { uri: string }, {
            format: options?.format ?? 'raw-list',
        });
    }

    public userDeleteBusinessContact(idArticle: number) {
        return this.jApi.article.delete(idArticle);
    }

    public userAddContact(idUser: number): Promise<ApiDefaultWrapper> {
        return this.network.post({ o: 'user', f: 'addContact', idUser } as OF);
    }

    public userRemoveContact(idUser: number): Promise<ApiDefaultWrapper> {
        return this.network.post({ o: 'user', f: 'removeContact', idUser } as OF);
    }

    /**
     * signIn function returns a Promise of users.
     * @param mail Mail or username
     * @param pwd Password to be validated
     * @param format Format of the response
     */
    public signIn<T extends jUserLittle = jUserView>(
        mail: string,
        pwd: string,
        format?: FormatList,
    ): Promise<ApiWrapper<T>> {
        return this.network.post({ o: 'user', f: 'signIn', mail, password: pwd, format: format ?? 'raw-view' } as OF);
    }

    public getFields(idUser: number, fieldsType: UserFieldsType) {
        return this.network.post<UserField[]>({
            o: 'user',
            f: 'getFields',
            idUser,
            fieldsType,
        } as OF);
    }

    public getSpots<T extends jGroupLittle = jGroupLittle>(
        { format = 'raw-little', ...payload }: { idUser?: number; format?: FormatList } = {},
        { signal }: { signal?: AbortSignal } = {},
    ): Promise<ApiWrapper<T[]>> {
        return this.network.post<T[]>(
            {
                o: 'user',
                f: 'getSpots',
                ...payload,
                format,
            } as OF,
            signal,
        );
    }

    public getNotificationsConfiguration<UserNotificationsConfiguration>(options?: {
        signal?: AbortSignal;
    }): Promise<ApiWrapper<UserNotificationsConfiguration>> {
        return this.network.post(
            {
                o: 'user',
                f: 'getNotificationsConfiguration',
            } as OF,
            options?.signal,
        );
    }

    public setNotificationsConfiguration<Boolean>(
        userNotificationsConfiguration: UserNotificationsConfiguration,
    ): Promise<ApiWrapper<Boolean>> {
        return this.network.postCSRF({
            o: 'user',
            f: 'setNotificationsConfiguration',
            userNotificationsConfiguration,
        } as OF);
    }
}
